import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, TextField, Button, Box, Typography, IconButton,
  InputAdornment, Tabs, Tab, Stack, ThemeProvider, CssBaseline, Divider,
  FormControlLabel, Checkbox, Link as MuiLink,
} from '@mui/material';
import { Email, Visibility, VisibilityOff, Lock, Close as CloseIcon, Person, Restaurant } from '@mui/icons-material';
import { landingTheme } from '../theme/landingTheme';

export default function LoginModal({ open, onLogin, onClose }) {
  const [tab, setTab] = useState(0); // 0: Sign In, 1: Sign Up
  // Sign In state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Sign Up state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirm, setSignupConfirm] = useState('');
  const [signupShowPassword, setSignupShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  // Error and loading
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Add new state for field-level errors
  const [signinErrors, setSigninErrors] = useState({ email: '', password: '' });
  const [signupErrors, setSignupErrors] = useState({ name: '', email: '', password: '', confirm: '' });

  const handleTabChange = (e, newValue) => {
    setTab(newValue);
    setError('');
    if (newValue === 1) setAgreeTerms(false);
  };

  const handleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.msg || 'Invalid email or password');
      }
      const data = await res.json();
      setLoading(false);
      onLogin(data.token, email);
    } catch (err) {
      setLoading(false);
      setError(err.message || 'Login failed');
    }
  };

  const handleSignup = async () => {
    setError('');
    if (!signupName.trim()) {
      setError('Name is required');
      return;
    }
    if (!signupEmail.includes('@')) {
      setError('Enter a valid email');
      return;
    }
    if (signupPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (signupPassword !== signupConfirm) {
      setError('Passwords do not match');
      return;
    }
    if (!agreeTerms) {
      setError('Please agree to the Terms & Conditions and Privacy Policy to continue');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: signupName, email: signupEmail, password: signupPassword })
      });
      if (res.status === 409) {
        setLoading(false);
        setError('Email already exists');
        return;
      }
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || data.msg || 'Signup failed');
      }
      // Signup successful, now log in the user automatically
      const loginRes = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: signupEmail, password: signupPassword })
      });
      if (!loginRes.ok) {
        const loginData = await loginRes.json();
        throw new Error(loginData.msg || 'Login after signup failed');
      }
      const loginData = await loginRes.json();
      setLoading(false);
      onLogin(loginData.token, signupEmail);
    } catch (err) {
      setLoading(false);
      setError(err.message || 'Signup failed');
    }
  };

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      // setIsAuthenticated is not defined in this component, so remove or handle accordingly.
      // If you want to notify parent, you could call onLogin or do nothing here.
      // For now, do nothing.
    };
    checkAuth();
    window.addEventListener('storage', checkAuth); // Listen for changes in other tabs
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  // Validation helpers
  const validateEmail = (email) => /.+@.+\..+/.test(email);
  const validatePassword = (password) => password.length >= 6;

  // Sign In field handlers
  const handleSigninEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setSigninErrors(errors => ({
      ...errors,
      email: !validateEmail(value) ? 'Enter a valid email' : ''
    }));
  };
  const handleSigninPasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setSigninErrors(errors => ({
      ...errors,
      password: !validatePassword(value) ? 'Password must be at least 6 characters' : ''
    }));
  };

  // Sign Up field handlers
  const handleSignupNameChange = (e) => {
    const value = e.target.value;
    setSignupName(value);
    setSignupErrors(errors => ({
      ...errors,
      name: !value.trim() ? 'Name is required' : ''
    }));
  };
  const handleSignupEmailChange = (e) => {
    const value = e.target.value;
    setSignupEmail(value);
    setSignupErrors(errors => ({
      ...errors,
      email: !validateEmail(value) ? 'Enter a valid email' : ''
    }));
  };
  const handleSignupPasswordChange = (e) => {
    const value = e.target.value;
    setSignupPassword(value);
    setSignupErrors(errors => ({
      ...errors,
      password: !validatePassword(value) ? 'Password must be at least 6 characters' : ''
    }));
    // Also update confirm password error if needed
    setSignupErrors(errors => ({
      ...errors,
      confirm: signupConfirm && value !== signupConfirm ? 'Passwords do not match' : ''
    }));
  };
  const handleSignupConfirmChange = (e) => {
    const value = e.target.value;
    setSignupConfirm(value);
    setSignupErrors(errors => ({
      ...errors,
      confirm: value !== signupPassword ? 'Passwords do not match' : ''
    }));
  };
  const handleSignupEmailBlur = async () => {
    if (validateEmail(signupEmail)) {
      const exists = await checkEmailExists(signupEmail);
      setSignupErrors(errors => ({
        ...errors,
        email: exists ? 'Email already exists' : ''
      }));
    }
  };

  const checkEmailExists = async (email) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      return data.exists; // backend should return { exists: true/false }
    } catch {
      return false; // fallback: assume not taken
    }
  };

  return (
    <ThemeProvider theme={landingTheme}>
      <CssBaseline />
    <Dialog open={open} maxWidth="xs" fullWidth onClose={onClose} PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ background: 'linear-gradient(135deg, #3730a3, #4f46e5)', px: 3, pt: 3, pb: 2, position: 'relative' }}>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'rgba(255,255,255,0.8)' }}>
          <CloseIcon />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <img src="/logo192.png" alt="HomeBillingPOS Logo" style={{ height: 30, width: 30, borderRadius: 6, backgroundColor: '#ffffff', padding: 2 }} />
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.2 }}>Home Billing POS</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>Manage branches, devices & licenses</Typography>
          </Box>
        </Stack>
        <Tabs value={tab} onChange={handleTabChange} variant="fullWidth" sx={{ '& .MuiTab-root': { color: 'rgba(255,255,255,0.7)', fontWeight: 600 }, '& .Mui-selected': { color: '#fff !important' }, '& .MuiTabs-indicator': { bgcolor: '#fff' } }}>
          <Tab label="Sign in" />
          <Tab label="Sign up" />
        </Tabs>
      </Box>
      <DialogContent sx={{ pt: 3 }}>
        {tab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email Address"
              type="email"
              value={email}
              onChange={handleSigninEmailChange}
              fullWidth
              autoFocus
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                )
              }}
              error={!!signinErrors.email}
              helperText={signinErrors.email || 'Enter your registered email address'}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handleSigninPasswordChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={!!signinErrors.password}
              helperText={signinErrors.password || 'Password must be at least 6 characters'}
            />
            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Box>
        )}
        {tab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Full Name"
              value={signupName}
              onChange={handleSignupNameChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                )
              }}
              error={!!signupErrors.name}
              helperText={signupErrors.name || 'Enter your full name'}
            />
            <TextField
              label="Email Address"
              type="email"
              value={signupEmail}
              onChange={handleSignupEmailChange}
              onBlur={handleSignupEmailBlur}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                )
              }}
              error={!!signupErrors.email}
              helperText={signupErrors.email || 'Enter a valid email address'}
            />
            <TextField
              label="Password"
              type={signupShowPassword ? 'text' : 'password'}
              value={signupPassword}
              onChange={handleSignupPasswordChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setSignupShowPassword((show) => !show)}
                      edge="end"
                    >
                      {signupShowPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={!!signupErrors.password}
              helperText={signupErrors.password || 'Password must be at least 6 characters'}
            />
            <TextField
              label="Confirm Password"
              type={signupShowPassword ? 'text' : 'password'}
              value={signupConfirm}
              onChange={handleSignupConfirmChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                )
              }}
              error={!!signupErrors.confirm}
              helperText={signupErrors.confirm || 'Re-enter your password'}
            />
            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0, gap: 0.5 }}
              control={
                <Checkbox
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  size="small"
                  sx={{ pt: 0.25 }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                  I agree to the{' '}
                  <MuiLink href="/terms" target="_blank" rel="noopener noreferrer" underline="hover" fontWeight={600}>
                    Terms &amp; Conditions
                  </MuiLink>
                  {' '}and{' '}
                  <MuiLink href="/privacy" target="_blank" rel="noopener noreferrer" underline="hover" fontWeight={600}>
                    Privacy Policy
                  </MuiLink>
                </Typography>
              }
            />
            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Box>
        )}
      </DialogContent>
      <Box sx={{ px: 3, pb: 3, pt: 1 }}>
        {tab === 0 ? (
          <Button variant="contained" fullWidth onClick={handleLogin} disabled={loading || !!signinErrors.email || !!signinErrors.password} size="large">
            {loading ? 'Signing in...' : 'Sign in to dashboard'}
          </Button>
        ) : (
          <Button
            variant="contained"
            fullWidth
            onClick={handleSignup}
            disabled={loading || !agreeTerms || !!signupErrors.name || !!signupErrors.email || !!signupErrors.password || !!signupErrors.confirm}
            size="large"
          >
            {loading ? 'Creating account...' : 'Start 7-day free trial'}
          </Button>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
          New accounts include a 7-day free trial and your Main branch. You will land on your dashboard after signup.
        </Typography>
      </Box>
    </Dialog>
    </ThemeProvider>
  );
} 